import React, { useState } from 'react';
import emailjs from 'emailjs-com';
import { Mail, Phone, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_USER_ID;

if (PUBLIC_KEY) {
    emailjs.init(PUBLIC_KEY);
}

const ContactSection = () => {
    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [errorMsg, setErrorMsg] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const name = form.name.trim();
        const email = form.email.trim();
        const message = form.message.trim();

        if (!name || !email || !message) {
            setStatus('error');
            setErrorMsg('Please fill in your name, email, and message.');
            return;
        }

        if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
            setStatus('error');
            setErrorMsg('Email service is not configured. Please contact us directly.');
            return;
        }

        setStatus('sending');
        setErrorMsg('');

        const composedMessage = `Email: ${email}\nMessage: ${message}`;

        try {
            await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
                from_name: name,
                from_email: email,
                message: composedMessage,
                to_email: 'support@vetobase.com',
            });
            setStatus('sent');
            setForm({ name: '', email: '', message: '' });
            setTimeout(() => setStatus('idle'), 4000);
        } catch (err) {
            setStatus('error');
            setErrorMsg(err?.text || 'Something went wrong. Please try again.');
        }
    };

    return (
        <section id="contact" className="w-full py-20 bg-white scroll-mt-24">
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="flex flex-col items-center gap-3 text-center mb-12">
                    <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">Get in Touch</span>
                    <h2 className="text-2xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Contact Us</h2>
                    <p className="text-slate-500 max-w-xl text-base md:text-lg">
                        Have questions about Vetobase AI? Send us a message — our team usually replies within a business day.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    {/* Contact details */}
                    <div className="lg:col-span-2 flex flex-col gap-4">
                        <a
                            href="mailto:support@vetobase.com"
                            className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-white hover:shadow-md transition-all"
                        >
                            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email</span>
                                <span className="text-base font-semibold text-slate-900 break-all">support@vetobase.com</span>
                            </div>
                        </a>

                        <a
                            href="tel:+923124012324"
                            className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-white hover:shadow-md transition-all"
                        >
                            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Phone</span>
                                <span className="text-base font-semibold text-slate-900">0312 4012324</span>
                            </div>
                        </a>
                    </div>

                    {/* Form */}
                    <form
                        onSubmit={handleSubmit}
                        className="lg:col-span-3 flex flex-col gap-4 bg-slate-50 border border-slate-100 rounded-3xl p-6 md:p-8"
                    >
                        <div className="flex flex-col gap-2">
                            <label htmlFor="name" className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                value={form.name}
                                onChange={handleChange}
                                placeholder="Your name"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="email" className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="message" className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                Message
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                required
                                rows={5}
                                value={form.message}
                                onChange={handleChange}
                                placeholder="How can we help?"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={status === 'sending'}
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-3.5 rounded-xl text-base font-bold hover:shadow-lg hover:shadow-blue-600/30 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {status === 'sending' ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Sending...
                                </>
                            ) : status === 'sent' ? (
                                <>
                                    <CheckCircle2 className="w-5 h-5" />
                                    Sent!
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <Send className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        {status === 'sent' && (
                            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                                <CheckCircle2 className="w-4 h-4" />
                                Thanks! Your message has been sent to support@vetobase.com.
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                                <AlertCircle className="w-4 h-4" />
                                {errorMsg}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
